/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-nested-ternary */
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  IconArrowUp,
  IconArrowDown,
  IconArrowsUpDown,
  IconAdjustmentsHorizontal,
  IconLoader2,
} from '@tabler/icons-react';
import { getErrorMessage } from '@common/utils';
import { ChevronDown } from 'lucide-react';
import { useDebounce } from 'use-debounce';
import { toast } from 'react-toastify';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LangContext } from '@/context/LangProvider';
import { QuestionAnswersColumns } from './QuestionAnswersColumns';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import DataTablePagination from '../paginations/DataTablePagination';
import { useGetInterviewQuestionTransripts } from '@/reactQuery/get';
import { CategoryApiResponse, SelectedCategory } from '@/types';
import useCategoryStore from '@/store/CategoryStore';

interface QuestionAnswersTableProps {
  questionID: string | undefined;
  selectedCategories: SelectedCategory[];
  setSelectedCategories: (category: SelectedCategory[]) => void;
  categories?: CategoryApiResponse;
  isPendingInterviewQuestions: boolean;
}

export default function QuestionAnswersTable({
  selectedCategories,
  setSelectedCategories,
  questionID,
  categories,
  isPendingInterviewQuestions,
}: QuestionAnswersTableProps) {
  const { project_id: projectId, workspace_id: workspaceId } = useParams();
  const [searchParams] = useSearchParams();
  const filterId = searchParams.get('filter_id');
  const categoryType = useCategoryStore((state) => state.categoryType);
  const { currentTheme } = useTheme();

  const {
    data: selectedQuestionTranscripts,
    error,
    isLoading: isTranscriptsLoading,
  } = useGetInterviewQuestionTransripts(filterId, categoryType, projectId, questionID);

  const isReady = !isPendingInterviewQuestions && !isTranscriptsLoading;

  useEffect(() => {
    if (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    }
  }, [error]);

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const { lang } = useContext(LangContext);
  const [areAllCategoriesZeroSingle, setAreAllCategoriesZeroSingle] = useState(false);
  const [areAllCategoriesZeroMulti, setAreAllCategoriesZeroMulti] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  // Debounced search for backend integration
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebounce(searchInput, 500);

  const transformedCategories = useMemo(
    () =>
      categories?.categories?.map((category: { id: string; label: string }) => ({
        label: category?.label,
        value: category?.id,
      })) || [],
    [categories],
  );

  const areAllCountsZeroCheck = useCallback(() => {
    if (!categories) return;
    // Check if all counts in 'categories' are 0
    const allCategoriesZero = categories.categories?.every(
      (category: any) => category?.count === 0,
    );
    // Check if all counts in 'categories_multi' are 0
    const allCategoriesMultiZero = categories?.categories_multi?.every(
      (category: any) => category?.count === 0,
    );

    // Set state based on whether all counts are zero
    setAreAllCategoriesZeroSingle(allCategoriesZero);
    setAreAllCategoriesZeroMulti(allCategoriesMultiZero);
  }, [categories, setAreAllCategoriesZeroSingle]);

  const isCategoryDataExists =
    categories?.categories?.length !== 0 || categories?.categories_multi?.length !== 0;
  const categoryDataSingle = categories?.categories;
  const categoryDataMultiple = categories?.categories_multi;

  // Filter logic
  const filteredTranscripts = useMemo(() => {
    if (selectedCategories?.length === 0) {
      return selectedQuestionTranscripts;
    }

    return selectedQuestionTranscripts?.filter((transcript) => {
      // Normalize transcript.category to be an array
      const transcriptCategories = Array.isArray(transcript?.category)
        ? transcript?.category
        : [transcript?.category];

      const selectedCategoryLabels = selectedCategories?.map((category) =>
        category?.label?.toLowerCase(),
      );

      const transcriptCategoryLabels = transcriptCategories?.map((cat) =>
        cat?.label?.toLowerCase(),
      );

      return transcriptCategoryLabels.some((label) => selectedCategoryLabels?.includes(label));
    });
  }, [selectedQuestionTranscripts, selectedCategories]);

  const columns = QuestionAnswersColumns(
    categoryDataSingle,
    questionID,
    categoryDataMultiple,
    isCategoryDataExists,
    areAllCategoriesZeroSingle,
    areAllCategoriesZeroMulti,
    transformedCategories,
    setSelectedCategories,
    selectedCategories,
    projectId,
    workspaceId,
  );

  const table = useReactTable({
    data: filteredTranscripts || [],
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
  });

  useEffect(() => {
    setColumnFilters(debouncedSearch ? [{ id: 'questions', value: debouncedSearch }] : []);
    table.setPageIndex(0);
  }, [debouncedSearch]);

  useEffect(() => {
    areAllCountsZeroCheck();
  }, [categories, areAllCountsZeroCheck]);

  if (!isReady) {
    return (
      <div className="flex items-center justify-center w-full py-20 mx-auto">
        <IconLoader2 style={{ color: currentTheme?.primary }} className="animate-spin" size={35} />
      </div>
    );
  }

  return (
    <div className="w-full mt-4">
      <div className="relative z-20 flex items-center justify-end w-full mb-3">
        <div className="flex items-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                <IconAdjustmentsHorizontal size={16} className="mr-2" />
                View
                <ChevronDown className="w-4 h-4 ml-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column?.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) => column.toggleVisibility(!!value)}
                  >
                    {column?.id}
                  </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="border rounded-md">
        <Table className="text-dark-text">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    <div className="flex items-center w-full gap-2">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {header?.id !== 'actions' && header?.id !== 'category' && (
                        <div>
                          {{
                            asc: <IconArrowUp size={18} />,
                            desc: <IconArrowDown size={18} />,
                          }[header.column.getIsSorted() as string] ?? (
                            <IconArrowsUpDown size={18} />
                          )}
                        </div>
                      )}
                      {header?.id === 'category' && (
                        <div className="self-start mt-3 -ml-6">
                          {{
                            asc: <IconArrowUp size={18} />,
                            desc: <IconArrowDown size={18} />,
                          }[header.column.getIsSorted() as string] ?? (
                            <IconArrowsUpDown size={18} />
                          )}
                        </div>
                      )}
                    </div>
                    {header?.id === 'questions' && (
                      <Input
                        placeholder="Search transcripts..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="w-full mb-2.5 min-w-52 max-w-52 bg-white"
                      />
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table?.getRowModel()?.rows?.length ? (
              table?.getRowModel()?.rows?.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns?.length} className="h-24 text-center">
                  {lang.get('msg.noResults')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination table={table} />
    </div>
  );
}
